import { useRef, useState } from "react";

/**
 * Drag-and-drop reordering of a short list with the browser's own drag events, from a grip handle, plus ArrowUp / ArrowDown on
 * the handle for the keyboard. `move(from, to)` is called with the item's old and new position.
 */
export function useDragReorder(length: number, move: (from: number, to: number) => void) {
  // The item being dragged is kept in a ref as well, so a drop is understood the moment it happens (state updates a beat later)
  const source = useRef<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);

  const end = () => {
    source.current = null;
    setDragging(null);
    setOver(null);
  };

  /** Put on each row: the row is where a dragged item can be dropped */
  const rowProps = (i: number) => ({
    "data-reorder-row": true,
    onDragOver: (e: React.DragEvent) => {
      if (source.current === null) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (over !== i) setOver(i);
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      if (source.current !== null && source.current !== i) move(source.current, i);
      end();
    },
  });

  /** Put on the grip button of each row */
  const handleProps = (i: number) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      source.current = i;
      setDragging(i);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(i));
      // Show the whole row under the pointer, not just the small handle
      const row = (e.currentTarget as HTMLElement).closest("[data-reorder-row]") as HTMLElement | null;
      if (row) e.dataTransfer.setDragImage(row, 16, 16);
    },
    onDragEnd: end,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "ArrowUp" && i > 0) {
        e.preventDefault();
        move(i, i - 1);
      } else if (e.key === "ArrowDown" && i < length - 1) {
        e.preventDefault();
        move(i, i + 1);
      }
    },
  });

  return { dragging, over, rowProps, handleProps };
}

/** The list with the item at `from` moved to `to` */
export function moved<T>(list: T[], from: number, to: number): T[] {
  const next = [...list];
  const [item] = next.splice(from, 1);
  if (item === undefined) return list;
  next.splice(to, 0, item);
  return next;
}
